import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from '../api/axios';
import { API_BASE_URL } from '../api/config';
import Logo from '../assets/images/Logo_HIRSCH.png';

export default function TermsConfirmation() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [checkboxes, setCheckboxes] = useState({
        hasSpace: false,
        willBePresent: false
    });

    const handleCheckboxChange = (field) => {
        setCheckboxes(prev => ({
            ...prev,
            [field]: !prev[field]
        }));
    };

    const isFormValid = checkboxes.hasSpace && checkboxes.willBePresent;

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!isFormValid) {
            toast.error('Bitte bestätigen Sie beide Bedingungen');
            return;
        }

        setIsSubmitting(true);

        try {
            await axios.put(`${API_BASE_URL}/orders/${id}/terms`);
            toast.success('Bedingungen erfolgreich bestätigt!');
            navigate('/checkout/success');
        } catch (error) {
            console.error('Error confirming terms:', error);
            toast.error('Fehler beim Bestätigen der Bedingungen. Bitte versuchen Sie es erneut.');
        } finally {
            setIsSubmitting(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            {/* Header */}
            <header className="bg-white shadow-sm">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="flex items-center justify-center h-32">
                        <img src={Logo} alt="Hirsch Logo" className="h-12 lg:h-24" />
                    </div>
                </div>
            </header>

            {/* Main Content */}
            <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
                <div className="bg-white rounded-lg shadow-md p-8">
                    <h1 className="text-3xl font-bold text-gray-900 mb-6">
                        Richtlinien und Bestätigungen
                    </h1>

                    <div className="prose max-w-none mb-8">
                        <h2 className="text-xl font-semibold text-gray-800 mb-4">
                            Wichtige Hinweise zur Containerlieferung
                        </h2>

                        <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6">
                            <h3 className="text-lg font-semibold text-blue-900 mb-2">
                                Platzbedarf
                            </h3>
                            <p className="text-gray-700">
                                Bitte stellen Sie sicher, dass ausreichend Platz für die Anlieferung und Aufstellung
                                des Containers vorhanden ist. Der Container benötigt eine ebene Fläche und ausreichend
                                Zufahrtsmöglichkeiten für unser Lieferfahrzeug.
                            </p>
                        </div>

                        <div className="bg-orange-50 border-l-4 border-orange-500 p-4 mb-6">
                            <h3 className="text-lg font-semibold text-orange-900 mb-2">
                                Anwesenheit vor Ort
                            </h3>
                            <p className="text-gray-700">
                                Am Liefertag muss eine ansprechbare Person vor Ort sein, um den genauen Stellplatz
                                anzuweisen und die Lieferung zu bestätigen. Sollte niemand anwesend sein, kann der
                                Container nicht zugestellt werden und es können zusätzliche Kosten entstehen.
                            </p>
                        </div>

                        <div className="bg-gray-50 border-l-4 border-gray-500 p-4 mb-6">
                            <h3 className="text-lg font-semibold text-gray-900 mb-2">
                                Allgemeine Bedingungen
                            </h3>
                            <ul className="list-disc list-inside text-gray-700 space-y-2">
                                <li>Die Zufahrt muss für LKW befahrbar sein</li>
                                <li>Überhangende Äste oder andere Hindernisse müssen entfernt werden</li>
                                <li>Der Untergrund muss tragfähig sein</li>
                                <li>Eventuelle Genehmigungen für öffentliche Flächen liegen in Ihrer Verantwortung</li>
                            </ul>
                        </div>
                    </div>

                    {/* Confirmation Form */}
                    <form onSubmit={handleSubmit} className="border-t pt-6">
                        <h2 className="text-xl font-semibold text-gray-900 mb-4">
                            Bestätigungen
                        </h2>

                        <div className="space-y-4 mb-6">
                            <label className="flex items-start space-x-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={checkboxes.hasSpace}
                                    onChange={() => handleCheckboxChange('hasSpace')}
                                    className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                />
                                <span className="text-gray-700 group-hover:text-gray-900">
                                    <strong>Ich bestätige, dass genug Platz vorhanden ist</strong> für die
                                    Anlieferung und Aufstellung des Containers gemäß den oben genannten Anforderungen.
                                </span>
                            </label>

                            <label className="flex items-start space-x-3 cursor-pointer group">
                                <input
                                    type="checkbox"
                                    checked={checkboxes.willBePresent}
                                    onChange={() => handleCheckboxChange('willBePresent')}
                                    className="mt-1 h-5 w-5 text-blue-600 focus:ring-blue-500 border-gray-300 rounded cursor-pointer"
                                />
                                <span className="text-gray-700 group-hover:text-gray-900">
                                    <strong>Ich bestätige, dass ich am Liefertag vor Ort sein werde</strong> oder
                                    eine bevollmächtigte Person für die Entgegennahme der Lieferung anwesend ist.
                                </span>
                            </label>
                        </div>

                        <div className="flex items-center justify-between pt-4">
                            <button
                                type="button"
                                onClick={() => navigate(-1)}
                                className="px-6 py-3 border border-gray-300 rounded-md shadow-sm text-base font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                            >
                                Zurück
                            </button>

                            <button
                                type="submit"
                                disabled={!isFormValid || isSubmitting}
                                className={`px-8 py-3 border border-transparent rounded-md shadow-sm text-base font-medium text-white
                                    ${isFormValid && !isSubmitting
                                        ? 'bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500'
                                        : 'bg-gray-300 cursor-not-allowed'
                                    }`}
                            >
                                {isSubmitting ? 'Wird bestätigt...' : 'Bestätigen'}
                            </button>
                        </div>
                    </form>
                </div>
            </main>
        </div>
    );
}
